import { useState } from 'react';
import { LogsList } from './LogsList';
import { LogsUserFilter } from './LogsUserFilter';
import { LogsFilters } from './LogsFilters';
import { LogsCategoryFilter } from './LogsCategoryFilter';
import { LogsOrganizationFilters } from './LogsOrganizationFilters';
import { LogsTimeFilter } from './LogsTimeFilter';
import { HabitSettingsDialog } from '@/components/admin/habits/HabitSettingsDialog';
import { Button } from '@/components/ui/button';
import { Search, Loader2, Settings2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export function LogsManagement() {
  const [userFilter, setUserFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [organizationFilter, setOrganizationFilter] = useState<string[]>([]);
  const [timeFilter, setTimeFilter] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [selectedLogs, setSelectedLogs] = useState<Set<string>>(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [showHabitSettings, setShowHabitSettings] = useState(false);
  const { t, dir, language } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('title')}
          </h2>
          <p className="text-muted-foreground">
            {t('description')}
          </p>
        </div>
        <Button 
          variant="outline"
          onClick={() => setShowHabitSettings(true)}
          className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
        >
          <Settings2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
          {isRTL ? 'إعدادات العادات' : 'Habits Settings'}
        </Button>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-2">
        <div className="flex-1 min-w-0">
          <LogsUserFilter 
            selected={userFilter} 
            onSelect={setUserFilter}
            title={t('filters.usersTitle')}
          />
        </div>
        <div className="flex-1 min-w-0">
          <LogsTimeFilter 
            value={timeFilter}
            onChange={setTimeFilter}
            title={t('filters.timeTitle')}
          />
        </div>
        <div className="flex-1 min-w-0">
          <LogsFilters 
            selected={typeFilter} 
            onSelect={setTypeFilter}
            title={t('filters.typeTitle')}
          />
        </div>
        <div className="flex-1 min-w-0">
          <LogsCategoryFilter 
            selected={categoryFilter} 
            onSelect={setCategoryFilter}
            title={t('filters.categoriesTitle')}
          />
        </div>
        <div className="flex-1 min-w-0">
          <LogsOrganizationFilters 
            selected={organizationFilter} 
            onSelect={setOrganizationFilter}
            title={t('filters.organizationsTitle')}
          />
        </div>
        <div className="flex-shrink-0">
          <Button 
            onClick={() => setIsSearching(true)}
            disabled={isSearching}
            className={`h-9 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
          >
            {isSearching ? (
              <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            ) : (
              <Search className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            )}
            {t('search')}
          </Button>
        </div>
      </div>

      <LogsList 
        userFilter={userFilter} 
        typeFilter={typeFilter} 
        categoryFilter={categoryFilter}
        organizationFilter={organizationFilter}
        timeFilter={timeFilter}
        onSelectionChange={setSelectedLogs}
        triggerSearch={isSearching}
        onSearchComplete={() => setIsSearching(false)}
      />

      {/* Habit Settings Dialog */}
      <HabitSettingsDialog 
        open={showHabitSettings} 
        onOpenChange={setShowHabitSettings} 
      />
    </div>
  );
}
